import React, { useState } from "react";
import { Menu, MenuItem, Typography, IconButton } from "@mui/material";
// import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useDrag } from "react-dnd";
import { IShiftDraggable } from "../../../models/response/IShiftManagementResponse";
import { observer } from "mobx-react";
import moment from "moment";
import { FormattedMessage } from "react-intl";

interface DraggableShiftProps {
  shift: IShiftDraggable;
  isScheduled?: boolean;
  onEdit?: (shiftId: number) => void;
  onRemove?: () => void;
  onChange?: () => void;
  onDelete?: (shiftId: number) => void;
  deleteToggle?: () => void;
  isDisabled?: boolean;
  isSchedulePublished?: boolean;
}

const DraggableShift: React.FC<DraggableShiftProps> = (props) => {
  let isSchedulePublished = props.isSchedulePublished
    ? props.isSchedulePublished
    : false;
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: "SHIFT",
      item: { ...props.shift, isScheduled: props.isScheduled },
      canDrag: !props.isDisabled && props.shift.ShiftStatus === 0,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [props.shift, props.isScheduled, props.isDisabled]
  );
 
  function getReadableTextColor(colorStr:any) {
    // Create a dummy element to let browser normalize the color
    const temp = document.createElement("div");
    temp.style.color = colorStr;
    document.body.appendChild(temp);

    // Get normalized rgb(...) value from browser
    const rgbStr = getComputedStyle(temp).color;
    document.body.removeChild(temp);

    const rgb = rgbStr.match(/\d+/g); // Extract numbers

    if (!rgb || rgb.length < 3) {
      // Fallback if invalid
      return "#000"; 
    }

    const [r, g, b] = rgb.map(Number);

    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.65 ? "#000" : "#fff";
  }

  return (
    <div
      ref={drag}
      className="schedule_drop_element"
      style={{
        backgroundColor: props.shift.Colour,
        cursor: props.isDisabled ? "not-allowed" : "move",
        opacity: isDragging ? 0.5 : 1,
        color: getReadableTextColor(props.shift.Colour),
      }}
    > 
      <div
      className="d-flex flex-column align-items-start justify-content-between"
      >
        {props.shift.ShiftShortName && (
          <div style={{color: props.shift.Colour, backgroundColor: getReadableTextColor(props.shift.Colour),}} className="shiftBadge">
            {props.shift.ShiftShortName}
            {isSchedulePublished && <span>*</span>}
          </div>
        )}
        {!props.isScheduled && (
          <Typography variant="body2">{props.shift.ShiftName}</Typography>
        )}
        {!props.isDisabled && !isSchedulePublished && (
          <div
            className="draggable_shift_menu_icon"
          >
            <MenuDropdown
              shiftId={props.shift.Id}
              onEdit={props.onEdit}
              onRemove={props.onRemove}
              onChange={props.onChange}
              onDelete={props.onDelete}
              deleteToggle={props.deleteToggle}
            />
          </div>
        )}
      </div>
      <Typography
        variant="caption"
      >
        <span className="shift_start_time">{moment(props.shift.StartTime, "HH:mm:ss").format("HH:mm")}</span>&nbsp;
        <span className="shift_time_seperator">-</span>&nbsp;
        <span className="shift_end_time">{moment(props.shift.EndTime, "HH:mm:ss").format("HH:mm")}</span>
      </Typography>
    </div>
  );
};

interface MenuDropdownProps {
  onEdit?: (shiftId: number) => void;
  onRemove?: () => void;
  onChange?: () => void;
  onDelete?: (shiftId: number) => void;
  deleteToggle?: () => void;
  shiftId: number;
}

const MenuDropdown: React.FC<MenuDropdownProps> = (props) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <div
        onClick={handleClick}
        style={{
          padding: 0,
        }}
        aria-controls={anchorEl ? "shift-menu" : undefined}
        aria-haspopup="true"
      >
        {/* Add a clickable icon */}
        <IconButton size="small">
          <svg
            id="fi_2311524"
            fill="currentColor"
            enableBackground="new 0 0 32 32"
            height="16"
            viewBox="0 0 32 32"
            width="16"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              id="XMLID_294_"
              d="m13 16c0 1.654 1.346 3 3 3s3-1.346 3-3-1.346-3-3-3-3 1.346-3 3z"
            ></path>
            <path
              id="XMLID_295_"
              d="m13 26c0 1.654 1.346 3 3 3s3-1.346 3-3-1.346-3-3-3-3 1.346-3 3z"
            ></path>
            <path
              id="XMLID_297_"
              d="m13 6c0 1.654 1.346 3 3 3s3-1.346 3-3-1.346-3-3-3-3 1.346-3 3z"
            ></path>
          </svg>
        </IconButton>
      </div>

      <Menu
        id="shift-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {props.onEdit && (
          <MenuItem
            onClick={() => {
              props.onEdit && props.onEdit(props.shiftId);
              handleClose();
            }}
          >
            <FormattedMessage id="edit" />
          </MenuItem>
        )}
        {props.onChange && (
          <MenuItem
            onClick={() => {
              props.onChange && props.onChange();
              handleClose();
            }}
          >
            <FormattedMessage id="shift_change_action" />
          </MenuItem>
        )}
        {props.onRemove && (
          <MenuItem
            onClick={() => {
              props.onRemove && props.onRemove();
              handleClose();
            }}
          >
            <FormattedMessage id="remove" />
          </MenuItem>
        )}
        {props.onDelete && (
          <MenuItem
            onClick={() => {
              props.onDelete && props.onDelete(props.shiftId);
              handleClose();
            }}
          >
            <FormattedMessage id="delete" />
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default observer(DraggableShift);
